/**
 * Sample budget mix by event — pie + legend, with a select to switch between events.
 * Rendered only when the benchmarks API returned per-event breakdowns.
 */
import React from 'react';
import { Card } from '../ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Calendar } from 'iconoir-react';
import { DASHBOARD_PALETTE } from './constants';

export default function EventBenchmarksCard({ data, selectedEvent, onSelect }) {
  const events = data?.event_benchmarks;
  if (!events || Object.keys(events).length === 0) return null;
  const current = selectedEvent && events[selectedEvent];

  return (
    <Card className="card-surface p-6" data-testid="event-benchmarks-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-muted" strokeWidth={1.5} />
          <h2 className="font-serif text-xl text-charcoal">Sample Budget Mix by Event</h2>
        </div>
        <select
          value={selectedEvent || ''}
          onChange={(e) => onSelect(e.target.value)}
          className="input-field text-sm py-1.5 max-w-[220px]"
          data-testid="event-benchmark-selector"
        >
          {Object.keys(events).map((ev) => (
            <option key={ev} value={ev}>{ev}</option>
          ))}
        </select>
      </div>
      {current && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={Object.entries(current).map(([k, v]) => ({ name: k, value: v }))}
                  cx="50%" cy="50%" innerRadius={50} outerRadius={90}
                  paddingAngle={2} dataKey="value"
                >
                  {Object.keys(current).map((_, i) => (
                    <Cell key={i} fill={DASHBOARD_PALETTE[i % DASHBOARD_PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => `${v}%`}
                  contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #CCC2BC', borderRadius: '4px', fontSize: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {Object.entries(current)
              .sort((a, b) => b[1] - a[1])
              .map(([cat, pct], i) => (
                <div key={cat} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DASHBOARD_PALETTE[i % DASHBOARD_PALETTE.length] }} />
                    <span className="text-charcoal">{cat}</span>
                  </div>
                  <span className="font-mono text-muted">{pct}%</span>
                </div>
              ))}
          </div>
        </div>
      )}
      <p className="text-xs text-muted mt-4 italic">
        Reference allocations commonly seen for {selectedEvent} — use as a starting point when planning each event's budget.
      </p>
    </Card>
  );
}
